import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BaseApi, BASE_URL } from '@edl-development/edl-common';
import { FormalCreateDto } from 'src/app/model/observation/formal/formal-create-dto.model';
import { FormalCreateNoteRequestDTO } from 'src/app/model/observation/formal/formal-create-note-request-dto.model';
import { FormalDistributeNotesDTO } from 'src/app/model/observation/formal/formal-distribute-notes-dto.model';
import { FormalNoteDTO } from 'src/app/model/observation/formal/formal-note-dto.model';
import { FormalSummaryUpdateDto } from 'src/app/model/observation/formal/formal-summary-update-dto.model';
import { RubricMasterDTO } from 'src/app/model/observation/rubric/rubric-master-dto.model';
import { FormalUpdateNoteRequestDTO } from 'src/app/model/observation/formal/formal-update-note-request-dto.model';
import { SignatureDTO } from 'src/app/model/signature-dto.model';
import { CreateNotificationDTO } from 'src/app/model/communication/create-notification-dto.model';
import { DateTimeDTO } from 'src/app/model/date-time-dto.model';
import { ObservationSummaryDTO } from 'src/app/model/observation/observation-summary-dto.model';

@Injectable()
export class FormalApi extends BaseApi {

    constructor(
        @Inject(BASE_URL) private baseUri: string,
        private http: HttpClient
    ) {
        super();
    }

    getDetails(userId: string, formalId: string) {
        return this.http.get(this.baseUri + '/observation/formal/' + formalId, {});
    }

    createFormal(createFormalDto: FormalCreateDto) {
        return this.http.post<ObservationSummaryDTO>(this.baseUri + '/observation/formal/create', createFormalDto);
    }

    beginSection(observationId: string, section: number, notification: CreateNotificationDTO = { additionalText: '' }) 
    {   
        return this.http.post(this.baseUri + '/observation/formal/' + observationId + '/section/' + section + "/begin", notification, { responseType: "text" });
    }

    completeSection(observationId: string, section: number, complete: boolean) {
        return this.http.put(this.baseUri + '/observation/formal/' + observationId + '/section/' + section + "/complete", complete, {});
    }

    scheduleSection(observationId: string, section: number, date: DateTimeDTO) {
        return this.http.put(this.baseUri + '/observation/formal/' + observationId + '/section/' + section + "/schedule", date, {});
    }

    submitSection(observationId: string, section: number) {
        return this.http.put(this.baseUri + '/observation/formal/' + observationId + '/section/' + section + "/submit", {});
    }

    skipSection(observationId: string, section: number) {
        return this.http.put(this.baseUri + '/observation/formal/' + observationId + '/section/' + section + "/skip", {});
    }

    restoreSection(observationId: string, section: number) {
        return this.http.put(this.baseUri + '/observation/formal/' + observationId + '/section/' + section + "/restore", {});
    }

    getNotes(observationId: string) {
        return this.http.get<FormalNoteDTO[]>(`${this.baseUri}/observation/formal/notes/${observationId}`);
    }

    addUnassignedNote(note: FormalCreateNoteRequestDTO) {
        return this.http.put<FormalNoteDTO>(`${this.baseUri}/observation/formal/addUnassignedNote`, note);
    }

    getRubricForFormal(observationId: string) {
        return this.http.get<RubricMasterDTO>(`${this.baseUri}/observation/formal/${observationId}/rubric`);
    }

    
    getSummary(observationId:string) {
        return this.http.get(`${this.baseUri}/observation/formal/${observationId}/summary`, { responseType: "text" });
    }

    createSummary(observationId:string){
        return this.http.post(`${this.baseUri}/observation/formal/${observationId}/summary`, { responseType: "text" });
    }

    saveSummary(summary: FormalSummaryUpdateDto) {
        return this.http.put(`${this.baseUri}/observation/formal/${summary.formalObservationID}/summary/save`, summary);
    }

    submitSummary(summary: FormalSummaryUpdateDto) {
        return this.http.put(`${this.baseUri}/observation/formal/${summary.formalObservationID}/summary/submit`, summary);
    }

    updateUnassignedNote(note: FormalUpdateNoteRequestDTO) {
        return this.http.put<FormalNoteDTO>(`${this.baseUri}/observation/formal/updateUnassignedNote`, note);
    }

    deleteUnassignedNote(noteId: string) {
        return this.http.delete<boolean>(`${this.baseUri}/observation/formal/deleteUnassignedNote/${noteId}`);
    }

    updateDistributeEvidenceDraft(formalId: string, notes: FormalDistributeNotesDTO[]) {
        return this.http.put<boolean>(`${this.baseUri}/observation/formal/updateDistributeNotesDrafts/${formalId}`, notes);
    }

    distributeUnassignedNote(formalId: string, notes: FormalDistributeNotesDTO[]) {
        return this.http.put<boolean>(`${this.baseUri}/observation/formal/distributeNotes/${formalId}`, notes);
    }

    authorizeFormal(formalId: string, signature: SignatureDTO) {
        return this.http.put<number>(`${this.baseUri}/observation/formal/${formalId}/authorize`, signature);
    }

    unauthorizeFormal(formalId: string) {
        return this.http.put<boolean>(`${this.baseUri}/observation/formal/${formalId}/unauthorize`, {});
    }
}
